import { useRef } from "react";
import {
  Box,
  Button,
  Drawer,
  Typography,
  IconButton,
  Divider,
  TextField,
  Radio,
  Stepper,
  Step,
  StepLabel,
  Avatar,
  Chip,
  CircularProgress,
  Snackbar,
  Alert,
  Paper,
  LinearProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import RestaurantIcon from "@mui/icons-material/Restaurant";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import TakeoutDiningIcon from "@mui/icons-material/TakeoutDining";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import MoneyIcon from "@mui/icons-material/Money";
import QrCodeScannerIcon from "@mui/icons-material/QrCodeScanner";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
import EditIcon from "@mui/icons-material/Edit";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  clearCart,
  addItem,
  removeItem,
  clearRestaurantCart,
  setRestaurant,
  selectCartItems,
  selectCartItemCount,
  selectCartSubtotal,
  selectFreeItemsCount,
  selectUpdateOrderId,
  updateCartWithOffers,
  clearUpdateOrderId,
} from "../../redux/slices/cartSlice";

import {
  validateOfferCode,
  applyOfferToCart,
  selectValidatedOffer,
  selectValidatingOffer,
  clearValidatedOffer,
  selectCartOfferCalculation,
  selectFreeItems,
  selectItemDiscounts,
  selectBogoDiscount,
  selectPromoDiscount,
  selectCartOffers,
  createWhatsAppOrder,
  clearCartCalculation,
} from "../../redux/slices/publicSlice";

import { fetchMyOrders } from "../../redux/slices/myOrdersSlice";
import { createPaymentApi, linkPaymentToOrderApi } from "../../api/paymentApi";
import { getOrCreateAnonymousId } from "../../utils/anonymousId";
import { useMyOrders } from "../../hooks/useMyOrders";

// ============================================
// HELPERS
// ============================================
const VegDot = ({ isVeg }) => (
  <Box
    sx={{
      width: 12,
      height: 12,
      flexShrink: 0,
      borderRadius: "2px",
      border: `1.5px solid ${isVeg ? "#2e7d32" : "#c62828"}`,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      mt: 0.3,
    }}
  >
    <Box
      sx={{
        width: 6,
        height: 6,
        borderRadius: "50%",
        bgcolor: isVeg ? "#2e7d32" : "#c62828",
      }}
    />
  </Box>
);

const ORDER_TYPES = [
  {
    value: "DINE_IN",
    label: "Dine In",
    emoji: "🍽",
    color: "#2e7d32",
    bg: "#e8f5e9",
    description: "Eat at restaurant",
    icon: <RestaurantIcon sx={{ fontSize: 28 }} />,
  },
  {
    value: "TAKEAWAY",
    label: "Takeaway",
    emoji: "🛍️",
    color: "#1565c0",
    bg: "#e3f2fd",
    description: "Pick up yourself",
    icon: <TakeoutDiningIcon sx={{ fontSize: 28 }} />,
  },
  {
    value: "DELIVERY",
    label: "Delivery",
    emoji: "🛵",
    color: "#e65100",
    bg: "#fff3e0",
    description: "Delivered to you",
    icon: <LocalShippingIcon sx={{ fontSize: 28 }} />,
  },
];

const PAYMENT_METHODS = [
  {
    value: "CASH",
    label: "Cash",
    icon: <MoneyIcon />,
    color: "#2E7D32",
    bg: "#E8F5E9",
    description: "Pay with cash at restaurant",
  },
  {
    value: "UPI",
    label: "UPI",
    icon: <QrCodeScannerIcon />,
    color: "#1565C0",
    bg: "#E3F2FD",
    description: "Scan QR code to pay",
  },
  {
    value: "PHONEPE",
    label: "PhonePe",
    icon: <PhoneIphoneIcon />,
    color: "#5F259F",
    bg: "#F3E5F5",
    description: "Pay with PhonePe",
  },
];

const getOriginalPrice = (item) => {
  if (item.originalPrice !== undefined && item.originalPrice !== null)
    return Number(item.originalPrice);
  if (item.basePrice !== undefined && item.basePrice !== null)
    return Number(item.basePrice);
  if (item.price !== undefined && item.price !== null)
    return Number(item.price);
  return 0;
};

const getFinalPrice = (item) => {
  if (isFreeItem(item)) return 0;
  if (item.discountedPrice !== undefined && item.discountedPrice !== null)
    return Number(item.discountedPrice);
  if (item.price !== undefined && item.price !== null)
    return Number(item.price);
  return 0;
};

const getQuantity = (item) => Number(item.qty || item.quantity || 1);

const isFreeItem = (item) => {
  if (item.isFree === true) return true;
  if (item.price === 0) return true;
  if (item.itemName?.includes("(FREE)")) return true;
  if (item.originalPrice > 0 && item.price === 0) return true;
  if (item.appliedOffer?.isFree === true) return true;
  if (item.appliedOffer?.offerCategory === "FREE_ITEM") return true;
  if (item.appliedOffer?.offerCategory === "BOGO" && item.price === 0)
    return true;
  return false;
};

// ============================================
// MAIN COMPONENT
// ============================================
const CartDrawer = ({ open, onClose, restaurantId, restaurantInfo }) => {
  const dispatch = useDispatch();
  const cart = useSelector(selectCartItems);
  const itemCount = useSelector(selectCartItemCount);
  const subtotal = useSelector(selectCartSubtotal);
  const freeItemsCount = useSelector(selectFreeItemsCount);
  const validatedOffer = useSelector(selectValidatedOffer);
  const validating = useSelector(selectValidatingOffer);
  const calculation = useSelector(selectCartOfferCalculation);
  const reduxBogoDiscount = useSelector(selectBogoDiscount);
  const reduxPromoDiscount = useSelector(selectPromoDiscount);
  const reduxFreeItems = useSelector(selectFreeItems);
  const cartOffers = useSelector(selectCartOffers);
  const reduxItemDiscounts = useSelector(selectItemDiscounts);
  const deliveryTime = restaurantInfo?.deliveryTime;
  const [copiedText, setCopiedText] = useState("");

  // Resolve real ObjectId and slug
  const actualDbRestaurantId = restaurantInfo?._id || restaurantId;
  const currentSlug =
    restaurantInfo?.slug ||
    (restaurantId !== restaurantInfo?._id ? restaurantId : null);

  // Update mode
  const updateOrderId = useSelector(selectUpdateOrderId);
  const isUpdateMode = !!updateOrderId;
  const { updateMyOrder } = useMyOrders();

  // Update mode: fetch previous order details
  const myOrders = useSelector((state) => state.myOrders?.orders) || [];
  const existingOrder = useMemo(() => {
    if (!isUpdateMode) return null;
    return myOrders.find((o) => (o._id || o.id) === updateOrderId) || null;
  }, [isUpdateMode, updateOrderId, myOrders]);

  // FIX: detect previous paid amount for update mode
  const previouslyPaidAmount = useMemo(() => {
    if (!existingOrder) return 0;

    const summary = existingOrder.orderSummary || {};

    if (Number(summary.paidAmount) > 0) {
      return Number(summary.paidAmount);
    }

    if (
      existingOrder.paymentStatus === "PAID" ||
      existingOrder.paymentMethod !== "CASH"
    ) {
      return Number(summary.totalAmount ?? existingOrder.totalAmount ?? 0);
    }

    return Number(summary.totalAmount ?? existingOrder.totalAmount ?? 0);
  }, [existingOrder]);

  // ============================================
  // APPLIED OFFERS
  // ============================================
  const [appliedOffers, setAppliedOffers] = useState([]);

  const finalAppliedOffers = useMemo(() => {
    if (appliedOffers?.length > 0) return appliedOffers;
    return calculation?.appliedOffers || [];
  }, [appliedOffers, calculation]);

  const itemDiscountsAmount = useMemo(() => {
    if (Number(reduxItemDiscounts) > 0) {
      return Number(reduxItemDiscounts);
    }
    const calculated = cart.reduce((total, item) => {
      if (isFreeItem(item)) return total;
      const originalPrice = getOriginalPrice(item);
      const finalPrice = getFinalPrice(item);
      const quantity = getQuantity(item);
      const discount = Math.max(0, originalPrice - finalPrice);
      return total + discount * quantity;
    }, 0);
    return Math.round(calculated * 100) / 100;
  }, [reduxItemDiscounts, cart]);

  const bogoDiscountAmount = useMemo(() => {
    return reduxBogoDiscount || 0;
  }, [reduxBogoDiscount]);

  const promoDiscountAmount = useMemo(() => {
    return reduxPromoDiscount || 0;
  }, [reduxPromoDiscount]);

  // ============================================
  // FREE ITEMS
  // ============================================
  const freeItemsFromOffers = useMemo(() => {
    if (calculation?.freeItems?.length > 0) {
      return calculation.freeItems;
    }
    if (reduxFreeItems?.length > 0) return reduxFreeItems;
    return [];
  }, [reduxFreeItems, calculation]);

  // ============================================
  // STATE
  // ============================================
  const [activeStep, setActiveStep] = useState(0);
  const [orderType, setOrderType] = useState("DINE_IN");
  const [customerDetails, setCustomerDetails] = useState({
    name: "",
    phone: "",
    address: "",
    tableNumber: "",
  });
  const [paymentMethod, setPaymentMethod] = useState("");
  const [paymentSettings, setPaymentSettings] = useState(null);
  const [loadingSettings, setLoadingSettings] = useState(false);
  const [paymentCompleted, setPaymentCompleted] = useState(false);
  const [paymentId, setPaymentId] = useState(null);
  const [isPlacingOrder, setIsPlacingOrder] = useState(false);
  const [promoCode, setPromoCode] = useState("");
  const [toast, setToast] = useState({
    open: false,
    msg: "",
    severity: "success",
  });

  // ============================================
  // DELIVERY SETTINGS (derived)
  // ============================================
  const dSettings = restaurantInfo?.deliverySettings;
  const deliveryAvailable = dSettings?.isDeliveryAvailable !== false;
  const minOrderEnabled = !!dSettings?.minOrderAmount?.enabled;
  const minOrderAmount = minOrderEnabled
    ? Number(dSettings?.minOrderAmount?.amount || 0)
    : 0;
  const deliveryCharge = Number(dSettings?.deliveryCharge || 0);
  const freeDeliveryThreshold = Number(dSettings?.freeDeliveryThreshold || 0);

  const netSubtotalForThreshold = useMemo(() => {
    return Math.max(
      0,
      Number(subtotal || 0) -
        (itemDiscountsAmount + bogoDiscountAmount + promoDiscountAmount),
    );
  }, [subtotal, itemDiscountsAmount, bogoDiscountAmount, promoDiscountAmount]);

  const isMinOrderMet =
    !minOrderEnabled || netSubtotalForThreshold >= minOrderAmount;
  const remainingForMinOrder = Math.max(
    0,
    minOrderAmount - netSubtotalForThreshold,
  );

  const willGetFreeDelivery =
    freeDeliveryThreshold > 0 &&
    netSubtotalForThreshold >= freeDeliveryThreshold;
  const remainingForFreeDelivery = Math.max(
    0,
    freeDeliveryThreshold - netSubtotalForThreshold,
  );

  // ============================================
  // EFFECTS
  // ============================================
  useEffect(() => {
    if (actualDbRestaurantId) {
      dispatch(setRestaurant(actualDbRestaurantId));
    }
  }, [actualDbRestaurantId, dispatch]);

  const lastCalcSignatureRef = useRef(null);

  useEffect(() => {
    if (!cart.length || !actualDbRestaurantId) return;

    const nonFreeItems = cart.filter((item) => !item.isFree);
    if (nonFreeItems.length === 0) return;

    const signature = nonFreeItems
      .map((i) => `${i._id}:${i.qty || i.quantity || 1}`)
      .sort()
      .join("|");

    if (lastCalcSignatureRef.current === signature) return;
    lastCalcSignatureRef.current = signature;

    const timer = setTimeout(() => {
      dispatch(
        applyOfferToCart({
          restaurantId: actualDbRestaurantId,
          cartItems: nonFreeItems,
          appliedOffers: [],
        }),
      );
    }, 300);

    return () => clearTimeout(timer);
  }, [cart, actualDbRestaurantId, dispatch]);

  useEffect(() => {
    if (isUpdateMode && existingOrder?.orderType) {
      setOrderType(existingOrder.orderType);
      if (existingOrder.paymentMethod) {
        setPaymentMethod(existingOrder.paymentMethod);
      }
      if (existingOrder.customer) {
        setCustomerDetails({
          name: existingOrder.customer.name || "",
          phone: existingOrder.customer.phone || "",
          address: existingOrder.customer.address || "",
          tableNumber: existingOrder.tableNumber
            ? String(existingOrder.tableNumber)
            : "",
        });
      }
    } else {
      const storedType = localStorage.getItem(
        `selected_order_type_${actualDbRestaurantId}`,
      );

      if (storedType && storedType !== "DELIVERY") {
        setOrderType(storedType);
      } else if (restaurantInfo?.orderType) {
        const availableTypes = ORDER_TYPES.filter(
          (type) => restaurantInfo.orderType[type.value] === true,
        );

        const preferredType =
          availableTypes.find((t) => t.value === "DINE_IN") ||
          availableTypes.find((t) => t.value === "TAKEAWAY") ||
          availableTypes[0];

        if (preferredType) {
          setOrderType(preferredType.value);
        }
      }
    }
  }, [isUpdateMode, existingOrder, restaurantInfo, actualDbRestaurantId]);

  const userData = useMemo(() => {
    try {
      const stored = localStorage.getItem("digital_manu_user_profile");
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  }, []);

  useEffect(() => {
    if (!open) {
      handleReset();
    }
  }, [open]);

 

  useEffect(() => {
    if (activeStep === 3 && actualDbRestaurantId) {
      loadPaymentSettings();
    }
  }, [activeStep, actualDbRestaurantId]);

  // ============================================
  // TOTAL & INCREMENTAL CALCULATION
  // ============================================
  const deliveryFee = useMemo(() => {
    if (orderType !== "DELIVERY") return 0;
    if (!deliveryAvailable) return 0;

    if (
      freeDeliveryThreshold > 0 &&
      netSubtotalForThreshold >= freeDeliveryThreshold
    ) {
      return 0;
    }
    return deliveryCharge;
  }, [
    orderType,
    deliveryAvailable,
    deliveryCharge,
    freeDeliveryThreshold,
    netSubtotalForThreshold,
  ]);

  const finalTotal = useMemo(() => {
    let base = 0;
    if (
      calculation?.finalTotal !== undefined &&
      calculation?.finalTotal !== null &&
      Number(calculation.finalTotal) > 0
    ) {
      base = Number(calculation.finalTotal);
    } else if (isUpdateMode) {
      const paidTotal = cart.reduce((total, item) => {
        if (isFreeItem(item)) return total;
        return total + getFinalPrice(item) * getQuantity(item);
      }, 0);
      const totalDiscounts =
        (bogoDiscountAmount || 0) + (promoDiscountAmount || 0);
      base = Math.max(0, paidTotal - totalDiscounts);
    } else {
      const totalAllDiscounts =
        (itemDiscountsAmount || 0) +
        (bogoDiscountAmount || 0) +
        (promoDiscountAmount || 0);
      base = Math.max(0, Number(subtotal || 0) - totalAllDiscounts);
    }

    return Math.round((base + deliveryFee) * 100) / 100;
  }, [
    calculation?.finalTotal,
    isUpdateMode,
    cart,
    subtotal,
    itemDiscountsAmount,
    bogoDiscountAmount,
    promoDiscountAmount,
    deliveryFee,
  ]);

  const formattedTotal = `₹${finalTotal.toFixed(2)}`;

  const incrementalBalance = useMemo(() => {
    if (!isUpdateMode || previouslyPaidAmount === 0) return finalTotal;
    return Math.max(
      0,
      Math.round((finalTotal - previouslyPaidAmount) * 100) / 100,
    );
  }, [isUpdateMode, previouslyPaidAmount, finalTotal]);

  const refundDueAmount = useMemo(() => {
    if (!isUpdateMode || previouslyPaidAmount === 0) return 0;
    return Math.max(
      0,
      Math.round((previouslyPaidAmount - finalTotal) * 100) / 100,
    );
  }, [isUpdateMode, previouslyPaidAmount, finalTotal]);

  const formattedBogoDiscount =
    bogoDiscountAmount > 0 ? `-₹${bogoDiscountAmount.toFixed(2)}` : null;
  const formattedPromoDiscount =
    promoDiscountAmount > 0 ? `-₹${promoDiscountAmount.toFixed(2)}` : null;

  const savingsMessage = useMemo(() => {
    const totalSavings =
      (itemDiscountsAmount || 0) +
      (bogoDiscountAmount || 0) +
      (promoDiscountAmount || 0);

    if (totalSavings > 0) {
      return `🎉 You saved ₹${totalSavings.toFixed(2)}!`;
    }
    return "";
  }, [itemDiscountsAmount, bogoDiscountAmount, promoDiscountAmount]);

  useEffect(() => {
    if (!open || !actualDbRestaurantId) return;
    if (!cart.length) return;

    const nonFreeItems = cart.filter((item) => !item.isFree);
    if (nonFreeItems.length === 0) return;

    lastCalcSignatureRef.current = null;

    dispatch(
      applyOfferToCart({
        restaurantId: actualDbRestaurantId,
        cartItems: nonFreeItems,
        appliedOffers: [],
      }),
    );
  }, [open, actualDbRestaurantId]);

  // ============================================
  // HANDLERS
  // ============================================
  const loadPaymentSettings = async () => {
    setLoadingSettings(true);
    try {
      const response = await import("../../api/paymentApi").then((module) =>
        module.getPaymentSettingsApi(actualDbRestaurantId),
      );
      setPaymentSettings(response.data.data || response.data);
    } catch (error) {
      setToast({
        open: true,
        msg: "Failed to load payment options",
        severity: "error",
      });
    } finally {
      setLoadingSettings(false);
    }
  };

  const handleReset = () => {
    setActiveStep(0);
    setOrderType("DINE_IN");
    setCustomerDetails({ name: "", phone: "", address: "", tableNumber: "" });
    setPaymentMethod("");
    setPaymentCompleted(false);
    setPaymentId(null);
    setIsPlacingOrder(false);
    setPromoCode("");
    setAppliedOffers([]);
    setCopiedText(""); 
  };

     // Copy Function
  const handleCopy = (text) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopiedText(text);
    setTimeout(() => setCopiedText(""), 2000);
  };

  const clearCompleteCart = () => {
    dispatch(clearCart());
    dispatch(clearRestaurantCart(actualDbRestaurantId));
    if (restaurantId) dispatch(clearRestaurantCart(restaurantId));
    if (currentSlug) dispatch(clearRestaurantCart(currentSlug));

    localStorage.removeItem(`cart_${actualDbRestaurantId}`);
    if (restaurantId) localStorage.removeItem(`cart_${restaurantId}`);
    if (currentSlug) localStorage.removeItem(`cart_${currentSlug}`);
  };

  const handleOrderTypeSelect = (type) => {
    // Guard: Delivery disabled
    if (type === "DELIVERY" && !deliveryAvailable) {
      setToast({
        open: true,
        msg: "Delivery is currently unavailable at this restaurant",
        severity: "warning",
      });
      return;
    }

    // Guard: Minimum order not met
    if (type === "DELIVERY" && minOrderEnabled && !isMinOrderMet) {
      setToast({
        open: true,
        msg: `Minimum order for delivery is ₹${minOrderAmount}. Add ₹${remainingForMinOrder.toFixed(2)} more to continue.`,
        severity: "warning",
      });
      return;
    }

    setOrderType(type);

    if (actualDbRestaurantId) {
      localStorage.setItem(`selected_order_type_${actualDbRestaurantId}`, type);
    }

    if (userData) {
      setCustomerDetails((prev) => ({
        ...prev,
        name: userData.name || "",
        phone: userData.phone || "",
        address: userData.address || "",
      }));
    }
    if (type !== "DINE_IN")
      setCustomerDetails((prev) => ({ ...prev, tableNumber: "" }));
    if (type !== "DELIVERY")
      setCustomerDetails((prev) => ({ ...prev, address: "" }));
    setActiveStep(2);
  };

  const handleNext = () => {
    if (activeStep === 0) {
      if (orderType === "DELIVERY") {
        if (!deliveryAvailable) {
          setToast({
            open: true,
            msg: "Delivery is currently unavailable at this restaurant.",
            severity: "warning",
          });
          return;
        }
        if (minOrderEnabled && !isMinOrderMet) {
          setToast({
            open: true,
            msg: `Minimum order amount for delivery is ₹${minOrderAmount}. Add ₹${remainingForMinOrder.toFixed(2)} more.`,
            severity: "warning",
          });
          return;
        }
      }
      if (isUpdateMode && refundDueAmount > 0) {
        setActiveStep(4);
        return;
      }
    }
    if (activeStep === 2) {
      if (!customerDetails.name?.trim()) {
        setToast({
          open: true,
          msg: "Please enter your name",
          severity: "warning",
        });
        return;
      }
      const phoneDigits = customerDetails.phone?.replace(/\D/g, "") || "";
      if (!customerDetails.phone?.trim() || phoneDigits.length < 10) {
        setToast({
          open: true,
          msg: "Enter a valid 10-digit phone number",
          severity: "warning",
        });
        return;
      }
      if (orderType === "DINE_IN" && !customerDetails.tableNumber?.trim()) {
        setToast({
          open: true,
          msg: "Please enter table number",
          severity: "warning",
        });
        return;
      }
      if (orderType === "DELIVERY" && !customerDetails.address?.trim()) {
        setToast({
          open: true,
          msg: "Please enter delivery address",
          severity: "warning",
        });
        return;
      }

      if (isUpdateMode && incrementalBalance === 0) {
        setActiveStep(4);
        return;
      }
    }
    if (activeStep === 3) {
      const needsPayment =
        !isUpdateMode || (isUpdateMode && incrementalBalance > 0);

      if (needsPayment && !paymentMethod) {
        setToast({
          open: true,
          msg: "Please select a payment method",
          severity: "warning",
        });
        return;
      }
    }
    setActiveStep((s) => s + 1);
  };

  const handleBack = () => {
    if (activeStep === 4 && isUpdateMode && incrementalBalance === 0) {
      setActiveStep(0);
      return;
    }
    setActiveStep((s) => s - 1);
  };

  const handleApplyPromo = async () => {
    if (!promoCode.trim()) return;

    try {
      const response = await dispatch(
        validateOfferCode({
          restaurantId: actualDbRestaurantId,
          offerCode: promoCode.trim().toUpperCase(),
          cartItems: cart,
          cartTotal: subtotal,
        }),
      ).unwrap();

      if (response?.appliedOffers) setAppliedOffers(response.appliedOffers);
      setPromoCode("");

      await dispatch(
        applyOfferToCart({
          restaurantId: actualDbRestaurantId,
          cartItems: cart,
          appliedOffers: response?.appliedOffers || [],
        }),
      ).unwrap();

      setToast({
        open: true,
        msg: "Promo code applied! 🎉",
        severity: "success",
      });
    } catch (err) {
      setToast({
        open: true,
        msg: err || "Invalid promo code",
        severity: "error",
      });
    }
  };

  const handleRemovePromo = () => {
    dispatch(clearValidatedOffer());
    setAppliedOffers([]);
    setPromoCode("");
    setToast({ open: true, msg: "Promo code removed", severity: "info" });
  };

  const handleAddItem = (item) => {
    const itemWithValidPrice = {
      ...item,
      qty: (item.qty || 1) + 1,
    };
    dispatch(addItem(itemWithValidPrice));
  };

  const handleRemoveItem = (id) => dispatch(removeItem(id));

  // ============================================
  // PLACE / UPDATE ORDER
  // ============================================
  const placeOrder = async () => {
    if (cart.length === 0) return;
    setIsPlacingOrder(true);

    try {
      if (cartOffers?.length > 0 && !isUpdateMode) {
        try {
          await dispatch(
            applyOfferToCart({
              restaurantId: actualDbRestaurantId,
              cartItems: cart,
              appliedOffers: cartOffers,
            }),
          ).unwrap();
        } catch (err) {
          console.error("Offer apply failed:", err);
        }
      }

      const orderItems = cart
        .filter((item) => !isFreeItem(item))
        .map((item) => {
          const originalPrice = getOriginalPrice(item);
          const finalPrice = getFinalPrice(item);
          const quantity = getQuantity(item);

          let selectedSize = null;
          if (item.selectedSize) {
            selectedSize =
              typeof item.selectedSize === "object"
                ? item.selectedSize.name
                : item.selectedSize;
          }

          let selectedVariant = null;
          if (item.selectedVariant) {
            selectedVariant =
              typeof item.selectedVariant === "object"
                ? item.selectedVariant.name
                : item.selectedVariant;
          }

          return {
            itemId: item._id,
            categoryId: item.categoryId || item.category?._id || item.category,
            itemName: item.itemName,
            quantity: quantity,
            selectedSize: selectedSize,
            selectedVariant: selectedVariant,
            selectedAddons: (item.selectedAddons || []).map((a) => ({
              name: a.name,
              price: Number(a.price || 0),
            })),
            appliedOffer: item.appliedOffer
              ? {
                  offerId: item.appliedOffer.offerId,
                  offerCode: item.appliedOffer.offerCode,
                  offerTitle: item.appliedOffer.offerTitle,
                  discountType: item.appliedOffer.discountType,
                  discountValue: item.appliedOffer.discountValue,
                  discountAmount: Number(
                    item.appliedOffer.discountAmount ||
                      item.discountAmount ||
                      0,
                  ),
                  isFree: false,
                }
              : null,
            originalPrice: originalPrice,
            price: finalPrice,
            total: finalPrice * quantity,
            isFree: false,
          };
        });

      const freeItems = (calculation?.freeItems || []).map((item) => ({
        itemId: item.itemId,
        itemName: item.itemName,
        quantity: item.quantity,
        price: item.price || item.originalPrice || 0,
        originalPrice: item.price || item.originalPrice || 0,
        offerId: item.offerId,
        offerCode: item.offerCode,
        offerTitle: item.offerTitle,
        offerCategory: item.offerCategory,
        discountAmount: item.discountAmount || 0,
      }));

      const calculatedSubtotal = orderItems.reduce((sum, item) => {
        const itemOriginalTotal =
          (item.originalPrice || item.price) * item.quantity;
        return sum + itemOriginalTotal;
      }, 0);

      const calculatedItemDiscounts = orderItems.reduce((sum, item) => {
        const originalPrice = Number(item.originalPrice || 0);
        const finalPrice = Number(item.price || 0);
        const quantity = Number(item.quantity || 1);

        if (originalPrice > finalPrice) {
          return sum + (originalPrice - finalPrice) * quantity;
        }
        return sum;
      }, 0);

      const calculatedFreeItemsTotal = freeItems.reduce(
        (sum, item) => sum + (item.price || 0) * (item.quantity || 1),
        0,
      );

      const calculatedBogoDiscount = bogoDiscountAmount || 0;
      const calculatedPromoDiscount = promoDiscountAmount || 0;

      const calculatedTotalDiscount =
        calculatedItemDiscounts +
        calculatedBogoDiscount +
        calculatedPromoDiscount;

      const calculatedGrandTotal = orderItems.reduce((sum, item) => {
        return sum + Number(item.price || 0) * Number(item.quantity || 1);
      }, 0);
      const roundedGrandTotal = Math.round(calculatedGrandTotal * 100) / 100;
      const roundedSubtotal = Math.round(calculatedSubtotal * 100) / 100;
      const roundedItemDiscounts =
        Math.round(calculatedItemDiscounts * 100) / 100;
      const roundedFreeItemsTotal =
        Math.round(calculatedFreeItemsTotal * 100) / 100;
      const roundedBogoDiscount =
        Math.round(calculatedBogoDiscount * 100) / 100;
      const roundedPromoDiscount =
        Math.round(calculatedPromoDiscount * 100) / 100;
      const roundedTotalDiscount =
        Math.round(calculatedTotalDiscount * 100) / 100;

      let paymentRecord = null;
      if (paymentMethod && !isUpdateMode) {
        try {
          const freshGrandTotal = finalTotal;

          const paymentData = {
            restaurantId: actualDbRestaurantId,
            method: paymentMethod,
            amount: freshGrandTotal,
            transactionDetails:
              paymentMethod === "CASH" ? "Cash payment" : "UPI payment",
            status: "PENDING",
          };
          const response = await createPaymentApi(paymentData);
          paymentRecord = response.data.data || response.data;
          setPaymentId(paymentRecord._id);
        } catch (paymentError) {
          console.error("Payment creation failed:", paymentError);
          setToast({
            open: true,
            msg: "Payment initialization failed. Please try again.",
            severity: "error",
          });
          setIsPlacingOrder(false);
          return;
        }
      } else if (isUpdateMode && incrementalBalance > 0 && paymentMethod) {
        try {
          const paymentData = {
            restaurantId: actualDbRestaurantId,
            method: paymentMethod,
            amount: incrementalBalance,
            transactionDetails: `Incremental payment for order update (Extra: ₹${incrementalBalance.toFixed(2)})`,
            status: "PENDING",
          };
          const response = await createPaymentApi(paymentData);
          paymentRecord = response.data.data || response.data;
          setPaymentId(paymentRecord._id);
        } catch (paymentError) {
          console.error("Incremental payment creation failed:", paymentError);
        }
      }

      const restaurantName = restaurantInfo?.restaurantName || "Restaurant";
      const anonId = getOrCreateAnonymousId();

      const allOffers =
        finalAppliedOffers?.length > 0
          ? finalAppliedOffers
          : calculation?.appliedOffers?.length > 0
            ? calculation.appliedOffers
            : cartOffers?.length > 0
              ? cartOffers
              : [];

      const cartLevelOffersOnly = allOffers.filter(
        (o) =>
          o.scope === "CART" ||
          o.offerCategory === "FREE_ITEM" ||
          o.offerCategory === "BOGO",
      );

      const orderData = {
        restaurantId: actualDbRestaurantId,
        restaurantName: restaurantName,
        restaurantWhatsappNumber:
          restaurantInfo?.whatsappNumber || restaurantInfo?.mobile || "",
        orderType,
        items: orderItems,
        freeItems: freeItems,
        anonymousId: anonId,
        customer: {
          name: customerDetails.name,
          phone: customerDetails.phone,
          ...(orderType === "DELIVERY" && {
            address: customerDetails.address,
          }),
        },
        ...(orderType === "DINE_IN" &&
          customerDetails.tableNumber && {
            table:
              parseInt(customerDetails.tableNumber) ||
              customerDetails.tableNumber,
          }),
        appliedOfferCode:
          validatedOffer?.offerCode ||
          cartLevelOffersOnly?.[0]?.offerCode ||
          null,
        paymentMethod: paymentMethod || "CASH",
        paymentId: paymentRecord?._id,
        subtotal: roundedSubtotal,
        discount:
          roundedItemDiscounts + roundedBogoDiscount + roundedPromoDiscount,
        freeItemsValue: roundedFreeItemsTotal,
        totalDiscount: roundedTotalDiscount,
        total: roundedGrandTotal,
        appliedOffers: cartLevelOffersOnly.map((o) => ({
          offerId: o.offerId || o._id,
          offerCode: o.offerCode,
          offerTitle: o.offerTitle || o.title,
          offerCategory: o.offerCategory,
          scope: o.scope,
          discountType: o.discountType,
          discountValue: o.discountValue || o.value,
          discountAmount: o.discountAmount || 0,
        })),
        bogoDiscount: roundedBogoDiscount,
        promoDiscount: roundedPromoDiscount,
      };

      // UPDATE MODE
      if (isUpdateMode) {
        try {
          const updateAppliedOffers = [
            ...(calculation?.appliedOffers || []),
            ...(finalAppliedOffers || []),
          ].filter(
            (v, i, a) => a.findIndex((t) => t.offerCode === v.offerCode) === i,
          );

          await updateMyOrder(updateOrderId, {
            items: orderItems,
            customer: {
              name: customerDetails.name,
              phone: customerDetails.phone,
              ...(orderType === "DELIVERY" && {
                address: customerDetails.address,
              }),
            },
            orderType: orderType,
            tableNumber:
              orderType === "DINE_IN" ? customerDetails.tableNumber : null,
            notes: "",
            appliedOffers: updateAppliedOffers,
            freeItems: freeItems || [],
            paymentId: paymentRecord?._id || undefined,
            paymentMethod: paymentMethod || undefined,
            incrementalAmount: incrementalBalance,
            previouslyPaidAmount: previouslyPaidAmount,
            refundDue: refundDueAmount,
          });

          clearCompleteCart();
          dispatch(clearValidatedOffer());
          dispatch(clearUpdateOrderId());
          await dispatch(fetchMyOrders());

          handleReset();
          onClose();

          setToast({
            open: true,
            msg:
              refundDueAmount > 0
                ? `Order updated! Refund of ₹${refundDueAmount.toFixed(2)} due from restaurant 🎉`
                : "Order updated successfully! 🎉",
            severity: "success",
          });

          setTimeout(() => {
            window.location.href = `/restaurant/${restaurantId}/orders`;
          }, 1200);

          return;
        } catch (err) {
          console.error("Update order failed:", err);
          setToast({
            open: true,
            msg: err?.message || "Update failed. Try again.",
            severity: "error",
          });
          setIsPlacingOrder(false);
          return;
        }
      }

      // NORMAL ORDER CREATE
      const result = await dispatch(createWhatsAppOrder(orderData)).unwrap();

      if (result.order && result.orderId) {
        saveOrderToLocalStorage(result);
      }

      if (paymentRecord && result.orderId) {
        try {
          await linkPaymentToOrderApi({
            paymentId: paymentRecord._id,
            orderId: result.orderId,
            restaurantId: actualDbRestaurantId,
          });
          setPaymentCompleted(true);
        } catch (linkError) {
          console.error("Failed to link payment to order:", linkError);
        }
      }

      if (
        restaurantInfo?.isWhatsAppNotifications !== false &&
        result.whatsappUrl
      ) {
        window.open(result.whatsappUrl, "_blank");
      }

      clearCompleteCart();
      dispatch(clearValidatedOffer());
      dispatch(clearCartCalculation());
      dispatch(fetchMyOrders());

      handleReset();
      onClose();

      const successMessage =
        restaurantInfo?.isWhatsAppNotifications !== false
          ? savingsMessage || "Order placed! WhatsApp opened 🎉"
          : "Order placed successfully! 🎉";

      setToast({
        open: true,
        msg: successMessage,
        severity: "success",
      });
    } catch (err) {
      console.error("Place order error:", err);
      const backendMessage =
        err?.response?.data?.message ||
        err?.data?.message ||
        err?.payload?.message ||
        err?.message ||
        (typeof err === "string" ? err : null);

      setToast({
        open: true,
        msg: backendMessage || "Failed to place order. Try again.",
        severity: "error",
      });
    } finally {
      setIsPlacingOrder(false);
    }
  };

  // ============================================
  // SAVE ORDER TO LOCALSTORAGE
  // ============================================
  const saveOrderToLocalStorage = (orderResult) => {
    try {
      const orderData = orderResult.order || {};
      const orderId = orderResult.orderId || orderData._id;
      const restaurant = orderData.restaurant || {};
      const orderSummary = orderData.orderSummary || {};

      const subtotalVal = Number(
        orderSummary.subtotal ?? orderData.subtotal ?? subtotal,
      );
      const discountVal = Number(
        orderSummary.totalDiscount ??
          orderData.totalDiscount ??
          itemDiscountsAmount + bogoDiscountAmount + promoDiscountAmount,
      );
      const totalVal = Number(
        orderSummary.totalAmount ?? orderData.totalAmount ?? finalTotal,
      );

      const newOrder = {
        id: orderId,
        _id: orderId,
        items: orderData.items || cart,
        total: totalVal,
        subtotal: subtotalVal,
        discount: discountVal,
        totalDiscount: discountVal,
        orderType: orderData.orderType || orderType,
        status: orderData.status || "PENDING",
        createdAt: orderData.createdAt || new Date().toISOString(),
        customer: orderData.customer || customerDetails,
        appliedOffers: orderData.appliedOffers || finalAppliedOffers,
        freeItems: orderData.freeItems || freeItemsFromOffers,
        isLatest: true,
        whatsappUrl: orderResult.whatsappUrl,
        restaurantWhatsappNumber:
          restaurant.whatsappNumber || restaurantInfo?.whatsappNumber || "",
        restaurantName:
          restaurant.restaurantName || restaurantInfo?.restaurantName || "",
        paymentMethod: orderData.paymentMethod || paymentMethod || "CASH",
        paymentStatus: orderData.paymentStatus || "PENDING",
        orderSummary: {
          subtotal: subtotalVal,
          totalDiscount: discountVal,
          totalAmount: totalVal,
        },
      };

      const keysToSave = [
        `orders_${actualDbRestaurantId}`,
        ...(currentSlug ? [`orders_${currentSlug}`] : []),
        ...(restaurantId ? [`orders_${restaurantId}`] : []),
      ];

      keysToSave.forEach((key) => {
        const savedOrders = JSON.parse(localStorage.getItem(key) || "[]");
        const filtered = savedOrders.filter(
          (order) => (order.id || order._id) !== newOrder.id,
        );
        const updatedOrders = [newOrder, ...filtered].slice(0, 10);
        localStorage.setItem(key, JSON.stringify(updatedOrders));
      });
    } catch (error) {
      console.error("Error saving to localStorage:", error);
    }
  };

  // ============================================
  // RENDER: CART
  // ============================================
  const renderCart = () => {
    const regularCartItems = cart.filter((item) => !isFreeItem(item));

    return (
      <>
        {regularCartItems.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 6 }}>
            <Typography sx={{ fontSize: "3rem" }}>🛒</Typography>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 700, mt: 1, color: "#333" }}
            >
              Your cart is empty
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Add items from the menu to continue
            </Typography>
          </Box>
        ) : (
          <>
            <Box sx={{ maxHeight: "35vh", overflow: "auto", mb: 1.5 }}>
              {regularCartItems.map((item) => {
                const originalPrice = getOriginalPrice(item);
                const finalPrice = getFinalPrice(item);
                const quantity = getQuantity(item);
                const itemTotal = finalPrice * quantity;
                const discountPerItem = originalPrice - finalPrice;

                return (
                  <Box
                    key={`${item._id}-${item.selectedSize || ""}-${item.selectedVariant || ""}`}
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      py: 1.5,
                      borderBottom: "1px solid #f5f5f5",
                      gap: 1.5,
                    }}
                  >
                    <Avatar
                      src={item.image || ""}
                      variant="rounded"
                      sx={{
                        width: 52,
                        height: 52,
                        bgcolor: "#f5f5f5",
                        border: "1px solid #ebebeb",
                        flexShrink: 0,
                      }}
                    >
                      {!item.image && (item.isVeg ? "🥗" : "🍗")}
                    </Avatar>

                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 0.5,
                          mb: 0.3,
                        }}
                      >
                        <VegDot isVeg={item.isVeg} />
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            fontSize: "0.85rem",
                            lineHeight: 1.3,
                          }}
                        >
                          {item.itemName}
                        </Typography>
                      </Box>

                      {(item.selectedSize ||
                        item.selectedVariant ||
                        item.selectedAddons?.length > 0) && (
                        <Typography
                          variant="caption"
                          sx={{
                            color: "#888",
                            fontSize: "0.7rem",
                            display: "block",
                            mb: 0.3,
                          }}
                        >
                          {[
                            item.selectedSize?.name &&
                              `Size: ${item.selectedSize.name}`,
                            item.selectedVariant?.name &&
                              `Variant: ${item.selectedVariant.name}`,
                            item.selectedAddons?.length > 0 &&
                              item.selectedAddons.map((a) => a.name).join(", "),
                          ]
                            .filter(Boolean)
                            .join(" · ")}
                        </Typography>
                      )}

                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          flexWrap: "wrap",
                        }}
                      >
                        {discountPerItem > 0 ? (
                          <>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 400,
                                color: "#999",
                                fontSize: "0.75rem",
                                textDecoration: "line-through",
                              }}
                            >
                              ₹{originalPrice.toFixed(2)}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 700,
                                color: "#2e7d32",
                                fontSize: "0.85rem",
                              }}
                            >
                              ₹{finalPrice.toFixed(2)}
                            </Typography>
                          </>
                        ) : (
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 700,
                              color: "#2e7d32",
                              fontSize: "0.85rem",
                            }}
                          >
                            ₹{finalPrice.toFixed(2)}
                          </Typography>
                        )}

                        {quantity > 1 && (
                          <Typography
                            component="span"
                            sx={{
                              fontWeight: 400,
                              color: "#888",
                              fontSize: "0.75rem",
                            }}
                          >
                            × {quantity} = ₹{itemTotal.toFixed(2)}
                          </Typography>
                        )}
                      </Box>
                    </Box>

                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        bgcolor: "#2e7d32",
                        borderRadius: 1.5,
                        overflow: "hidden",
                        height: 30,
                        flexShrink: 0,
                      }}
                    >
                      <IconButton
                        size="small"
                        onClick={() => handleRemoveItem(item._id)}
                        sx={{
                          color: "white",
                          borderRadius: 0,
                          width: 28,
                          height: 28,
                          "&:hover": { bgcolor: "#1b5e20" },
                        }}
                      >
                        {quantity === 1 ? (
                          <DeleteOutlineIcon sx={{ fontSize: 14 }} />
                        ) : (
                          <RemoveIcon sx={{ fontSize: 14 }} />
                        )}
                      </IconButton>
                      <Typography
                        sx={{
                          color: "white",
                          fontWeight: 800,
                          px: 0.8,
                          fontSize: "0.85rem",
                          minWidth: 22,
                          textAlign: "center",
                        }}
                      >
                        {quantity}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => handleAddItem(item)}
                        sx={{
                          color: "white",
                          borderRadius: 0,
                          width: 28,
                          height: 28,
                          "&:hover": { bgcolor: "#1b5e20" },
                        }}
                      >
                        <AddIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Box>
                  </Box>
                );
              })}
            </Box>

            {/* PROMO CODE */}
            <Box
              sx={{
                bgcolor: "#f9f9f9",
                borderRadius: 2,
                p: 1.5,
                mb: 1.5,
                border: "1px solid #f0f0f0",
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 700,
                  color: "#333",
                  mb: 1,
                  display: "block",
                  fontSize: "0.75rem",
                }}
              >
                🎟️ Promo Code
              </Typography>
              {validatedOffer ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CheckCircleIcon sx={{ fontSize: 18, color: "#2e7d32" }} />
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 700,
                        color: "#2e7d32",
                        display: "block",
                        fontSize: "0.78rem",
                      }}
                    >
                      "{validatedOffer.offerCode}" applied!
                    </Typography>
                    {promoDiscountAmount > 0 && (
                      <Typography
                        variant="caption"
                        sx={{ color: "#555", fontSize: "0.7rem" }}
                      >
                        Extra ₹{promoDiscountAmount.toFixed(2)} off!
                      </Typography>
                    )}
                  </Box>
                  <IconButton
                    size="small"
                    onClick={handleRemovePromo}
                    sx={{ p: 0.3, color: "#aaa" }}
                  >
                    <CloseIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Box>
              ) : (
                <Box sx={{ display: "flex", gap: 1 }}>
                  <TextField
                    size="small"
                    placeholder="Enter promo code"
                    value={promoCode}
                    onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                    onKeyDown={(e) => e.key === "Enter" && handleApplyPromo()}
                    sx={{
                      flex: 1,
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 1.5,
                        fontSize: "0.82rem",
                        height: 34,
                        "& fieldset": { borderColor: "#e0e0e0" },
                        "&:hover fieldset": { borderColor: "#2e7d32" },
                        "&.Mui-focused fieldset": { borderColor: "#2e7d32" },
                      },
                    }}
                    inputProps={{
                      style: {
                        fontWeight: 700,
                        letterSpacing: 1.2,
                        textTransform: "uppercase",
                        padding: "7px 12px",
                      },
                    }}
                  />
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={handleApplyPromo}
                    disabled={validating || !promoCode.trim()}
                    sx={{
                      borderRadius: 1.5,
                      textTransform: "none",
                      fontWeight: 700,
                      fontSize: "0.78rem",
                      borderColor: "#2e7d32",
                      color: "#2e7d32",
                      height: 34,
                      minWidth: 64,
                      "&:hover": { bgcolor: "#e8f5e9" },
                    }}
                  >
                    {validating ? (
                      <CircularProgress size={14} sx={{ color: "#2e7d32" }} />
                    ) : (
                      "Apply"
                    )}
                  </Button>
                </Box>
              )}
            </Box>

            {/* BILL SUMMARY */}
            <Box
              sx={{
                bgcolor: "#f9f9f9",
                borderRadius: 2,
                p: 1.5,
                border: "1px solid #f0f0f0",
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 700,
                  color: "#333",
                  mb: 1,
                  display: "block",
                  fontSize: "0.75rem",
                  textTransform: "uppercase",
                  letterSpacing: 0.5,
                }}
              >
                Bill Summary
              </Typography>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  mb: 0.6,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{ color: "#666", fontSize: "0.8rem" }}
                >
                  Subtotal ({itemCount} items)
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 600, fontSize: "0.8rem" }}
                >
                  ₹{subtotal.toFixed(2)}
                </Typography>
              </Box>

              {itemDiscountsAmount > 0 && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 0.6,
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{ color: "#2e7d32", fontSize: "0.8rem" }}
                  >
                    Item discounts
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "#2e7d32",
                      fontWeight: 700,
                      fontSize: "0.8rem",
                    }}
                  >
                    -₹{itemDiscountsAmount.toFixed(2)}
                  </Typography>
                </Box>
              )}

              {bogoDiscountAmount > 0 && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 0.6,
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{ color: "#2e7d32", fontSize: "0.8rem" }}
                  >
                    BOGO offer
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "#2e7d32",
                      fontWeight: 700,
                      fontSize: "0.8rem",
                    }}
                  >
                    {formattedBogoDiscount}
                  </Typography>
                </Box>
              )}

              {promoDiscountAmount > 0 && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 0.6,
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{ color: "#2e7d32", fontSize: "0.8rem" }}
                  >
                    Promo discount
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "#2e7d32",
                      fontWeight: 700,
                      fontSize: "0.8rem",
                    }}
                  >
                    {formattedPromoDiscount}
                  </Typography>
                </Box>
              )}

              {orderType === "DELIVERY" && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 0.6,
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{ color: "#666", fontSize: "0.8rem" }}
                  >
                    Delivery Fee
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ fontWeight: 700, fontSize: "0.8rem" }}
                  >
                    {deliveryFee === 0 ? (
                      <span style={{ color: "#2e7d32" }}>FREE</span>
                    ) : (
                      `₹${deliveryFee.toFixed(2)}`
                    )}
                  </Typography>
                </Box>
              )}

              <Divider sx={{ my: 0.8 }} />

              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography sx={{ fontWeight: 700, fontSize: "0.88rem" }}>
                  Total to Pay
                </Typography>
                <Typography
                  sx={{
                    fontWeight: 800,
                    fontSize: "0.95rem",
                    color: "#1a1a1a",
                  }}
                >
                  {formattedTotal}
                </Typography>
              </Box>

              {isUpdateMode && previouslyPaidAmount > 0 && (
                <Box
                  sx={{
                    mt: 1.5,
                    p: 1.2,
                    bgcolor: "#fff8f0",
                    borderRadius: 2,
                    border: "1px dashed #FF6B00",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 0.5,
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{ color: "#666", fontSize: "0.78rem" }}
                    >
                      Already Paid Earlier:
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 700,
                        color: "#2e7d32",
                        fontSize: "0.78rem",
                      }}
                    >
                      -₹{previouslyPaidAmount.toFixed(2)}
                    </Typography>
                  </Box>

                  <Divider sx={{ my: 0.5, borderColor: "#FF6B0030" }} />

                  {incrementalBalance > 0 ? (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 800,
                          color: "#e65100",
                          fontSize: "0.85rem",
                        }}
                      >
                        Remaining to Pay:
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 800,
                          color: "#e65100",
                          fontSize: "0.95rem",
                        }}
                      >
                        ₹{incrementalBalance.toFixed(2)}
                      </Typography>
                    </Box>
                  ) : refundDueAmount > 0 ? (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 800,
                          color: "#2e7d32",
                          fontSize: "0.85rem",
                        }}
                      >
                        Refund Due:
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 800,
                          color: "#2e7d32",
                          fontSize: "0.95rem",
                        }}
                      >
                        ₹{refundDueAmount.toFixed(2)}
                      </Typography>
                    </Box>
                  ) : (
                    <Typography
                      variant="caption"
                      sx={{
                        color: "#2e7d32",
                        fontWeight: 700,
                        display: "block",
                      }}
                    >
                      ✓ Full bill adjusted. No payment needed!
                    </Typography>
                  )}
                </Box>
              )}

              {savingsMessage && (
                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    textAlign: "right",
                    color: "#2e7d32",
                    mt: 0.5,
                    fontSize: "0.7rem",
                    fontWeight: 600,
                  }}
                >
                  {savingsMessage}
                </Typography>
              )}
            </Box>

            {freeItemsFromOffers.length > 0 && (
              <Box
                sx={{
                  bgcolor: "#e8f5e9",
                  borderRadius: 2,
                  p: 1.5,
                  mt: 1.5,
                  border: "1px solid #c8e6c9",
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 700,
                    color: "#2e7d32",
                    mb: 1,
                    display: "block",
                    fontSize: "0.75rem",
                  }}
                >
                  🎁 Free Items You'll Get
                </Typography>
                {freeItemsFromOffers.map((item, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 0.5,
                    }}
                  >
                    <Typography variant="caption" sx={{ fontSize: "0.75rem" }}>
                      {item.itemName} × {item.quantity}
                    </Typography>
                    <Chip
                      label={`FREE (₹${((item.price || item.originalPrice || 0) * item.quantity).toFixed(2)} saved)`}
                      size="small"
                      sx={{
                        height: 18,
                        fontSize: "0.6rem",
                        bgcolor: "#2e7d32",
                        color: "white",
                      }}
                    />
                  </Box>
                ))}
              </Box>
            )}
          </>
        )}
      </>
    );
  };

  // ============================================
  // RENDER: ORDER TYPE
  // ============================================
  const renderOrderType = () => {
    const availableOrderTypes = ORDER_TYPES.filter((type) => {
      if (!restaurantInfo?.orderType) return true;
      return restaurantInfo.orderType[type.value] === true;
    });

    if (availableOrderTypes.length === 0) {
      return (
        <Box sx={{ mt: 1, textAlign: "center", py: 3 }}>
          <Typography variant="body2" sx={{ color: "#666" }}>
            No order types available at this time.
          </Typography>
        </Box>
      );
    }

    const isDeliverySelected = orderType === "DELIVERY";

    return (
      <Box sx={{ mt: 1 }}>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 700,
            fontSize: "0.82rem",
            color: "#555",
            mb: 1.5,
            textAlign: "center",
          }}
        >
          How would you like your order?
        </Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          {availableOrderTypes.map((type) => {
            const isDelivery = type.value === "DELIVERY";
            const isDeliveryDisabled = isDelivery && !deliveryAvailable;
            const isSelected = orderType === type.value;

            return (
              <Box
                key={type.value}
                flex={1}
                onClick={() => handleOrderTypeSelect(type.value)}
                sx={{
                  cursor: isDeliveryDisabled ? "not-allowed" : "pointer",
                  borderRadius: 2.5,
                  p: 1.5,
                  border: isSelected
                    ? `2px solid ${type.color}`
                    : "1.5px solid #ebebeb",
                  bgcolor: isSelected ? type.bg : "white",
                  textAlign: "center",
                  transition: "all 0.15s",
                  opacity: isDeliveryDisabled ? 0.5 : 1,
                  position: "relative",
                  "&:active": {
                    transform: isDeliveryDisabled ? "none" : "scale(0.97)",
                  },
                }}
              >
                {isDeliveryDisabled && (
                  <Chip
                    label="OFF"
                    size="small"
                    sx={{
                      position: "absolute",
                      top: 4,
                      right: 4,
                      height: 16,
                      fontSize: "0.55rem",
                      fontWeight: 800,
                      bgcolor: "#ffebee",
                      color: "#c62828",
                    }}
                  />
                )}

                <Box sx={{ fontSize: "1.8rem", mb: 0.5 }}>{type.emoji}</Box>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 700,
                    fontSize: "0.75rem",
                    color: isSelected ? type.color : "#555",
                    display: "block",
                  }}
                >
                  {type.label}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ fontSize: "0.65rem", color: "#aaa" }}
                >
                  {type.description}
                </Typography>

                {isDelivery && !isDeliveryDisabled && (
                  <Box
                    sx={{
                      mt: 0.8,
                      pt: 0.6,
                      borderTop: "1px dashed #e0e0e0",
                    }}
                  >
                    {minOrderEnabled && minOrderAmount > 0 && (
                      <Typography
                        variant="caption"
                        sx={{
                          display: "block",
                          fontSize: "0.6rem",
                          fontWeight: 700,
                          color: isMinOrderMet ? "#2e7d32" : "#c62828",
                          lineHeight: 1.3,
                        }}
                      >
                        Min ₹{minOrderAmount}
                      </Typography>
                    )}
                    {freeDeliveryThreshold > 0 && (
                      <Typography
                        variant="caption"
                        sx={{
                          display: "block",
                          fontSize: "0.6rem",
                          fontWeight: 700,
                          color: willGetFreeDelivery ? "#2e7d32" : "#888",
                          lineHeight: 1.3,
                        }}
                      >
                        Free above ₹{freeDeliveryThreshold}
                      </Typography>
                    )}
                    {deliveryCharge > 0 &&
                      !willGetFreeDelivery &&
                      freeDeliveryThreshold === 0 && (
                        <Typography
                          variant="caption"
                          sx={{
                            display: "block",
                            fontSize: "0.6rem",
                            fontWeight: 700,
                            color: "#888",
                            lineHeight: 1.3,
                          }}
                        >
                          ₹{deliveryCharge} charge
                        </Typography>
                      )}
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>

        {/* Delivery Selected — Info & Warnings */}
        {isDeliverySelected && deliveryAvailable && (
          <Box sx={{ mt: 2 }}>
            {/* Min Order Not Met Warning */}
            {minOrderEnabled && !isMinOrderMet && (
              <Box
                sx={{
                  p: 1.5,
                  bgcolor: "#ffebee",
                  borderRadius: 2,
                  border: "1px solid #ef9a9a",
                  mb: 1.2,
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 1,
                }}
              >
                <WarningAmberIcon
                  sx={{
                    fontSize: 20,
                    color: "#c62828",
                    mt: 0.1,
                    flexShrink: 0,
                  }}
                />
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 800,
                      color: "#c62828",
                      fontSize: "0.82rem",
                      mb: 0.3,
                    }}
                  >
                    Minimum order not met
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "#c62828",
                      display: "block",
                      fontSize: "0.72rem",
                      lineHeight: 1.4,
                    }}
                  >
                    Minimum order for delivery is <b>₹{minOrderAmount}</b>. Your
                    current total is{" "}
                    <b>₹{netSubtotalForThreshold.toFixed(2)}</b>.
                  </Typography>
                  <Box
                    sx={{
                      mt: 1,
                      p: 0.8,
                      bgcolor: "white",
                      borderRadius: 1,
                      display: "inline-block",
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 800,
                        color: "#c62828",
                        fontSize: "0.75rem",
                      }}
                    >
                      Add ₹{remainingForMinOrder.toFixed(2)} more →
                    </Typography>
                  </Box>
                </Box>
              </Box>
            )}

            {/* Free Delivery Progress */}
            {(!minOrderEnabled || isMinOrderMet) &&
              freeDeliveryThreshold > 0 && (
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    border: willGetFreeDelivery
                      ? "1px solid #a5d6a7"
                      : "1px solid #ffe082",
                    bgcolor: willGetFreeDelivery ? "#e8f5e9" : "#fff8e1",
                  }}
                >
                  {willGetFreeDelivery ? (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography sx={{ fontSize: "1.2rem" }}>🎉</Typography>
                      <Box>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 800,
                            color: "#2e7d32",
                            fontSize: "0.82rem",
                          }}
                        >
                          You got FREE delivery!
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            color: "#2e7d32",
                            fontSize: "0.72rem",
                          }}
                        >
                          Orders above ₹{freeDeliveryThreshold} get free
                          delivery
                        </Typography>
                      </Box>
                    </Box>
                  ) : (
                    <>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          mb: 1,
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.8,
                          }}
                        >
                          <Typography sx={{ fontSize: "1.1rem" }}>
                            🛵
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              fontWeight: 800,
                              color: "#e65100",
                              fontSize: "0.78rem",
                            }}
                          >
                            Add ₹{remainingForFreeDelivery.toFixed(2)} more for
                            FREE delivery
                          </Typography>
                        </Box>
                      </Box>

                      <LinearProgress
                        variant="determinate"
                        value={Math.min(
                          100,
                          (netSubtotalForThreshold / freeDeliveryThreshold) *
                            100,
                        )}
                        sx={{
                          height: 6,
                          borderRadius: 3,
                          bgcolor: "#ffe0b2",
                          "& .MuiLinearProgress-bar": {
                            bgcolor: "#f57c00",
                            borderRadius: 3,
                          },
                        }}
                      />

                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          mt: 0.6,
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{ fontSize: "0.65rem", color: "#888" }}
                        >
                          ₹{netSubtotalForThreshold.toFixed(0)}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            fontSize: "0.65rem",
                            color: "#888",
                            fontWeight: 700,
                          }}
                        >
                          ₹{freeDeliveryThreshold}
                        </Typography>
                      </Box>

                      <Typography
                        variant="caption"
                        sx={{
                          display: "block",
                          mt: 0.8,
                          fontSize: "0.68rem",
                          color: "#e65100",
                          fontStyle: "italic",
                        }}
                      >
                        Otherwise a ₹{deliveryCharge} delivery charge will apply
                      </Typography>
                    </>
                  )}
                </Box>
              )}

            {/* Delivery charge info (when no threshold set) */}
            {(!minOrderEnabled || isMinOrderMet) &&
              freeDeliveryThreshold === 0 &&
              deliveryCharge > 0 && (
                <Box
                  sx={{
                    p: 1.2,
                    bgcolor: "#fff8e1",
                    borderRadius: 2,
                    border: "1px solid #ffe082",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <LocalOfferIcon sx={{ fontSize: 16, color: "#e65100" }} />
                  <Typography
                    variant="caption"
                    sx={{
                      color: "#e65100",
                      fontWeight: 700,
                      fontSize: "0.75rem",
                    }}
                  >
                    Delivery charge: ₹{deliveryCharge}
                  </Typography>
                </Box>
              )}
          </Box>
        )}

        {/* Delivery is OFF info */}
        {isDeliverySelected && !deliveryAvailable && (
          <Box
            sx={{
              mt: 2,
              p: 1.5,
              bgcolor: "#ffebee",
              borderRadius: 2,
              border: "1px solid #ef9a9a",
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <WarningAmberIcon sx={{ fontSize: 20, color: "#c62828" }} />
            <Typography
              variant="caption"
              sx={{ color: "#c62828", fontWeight: 700, fontSize: "0.75rem" }}
            >
              Delivery is currently unavailable at this restaurant
            </Typography>
          </Box>
        )}
      </Box>
    );
  };

  // ============================================
  // RENDER: DETAILS
  // ============================================
  const renderDetails = () => {
    const currentType = ORDER_TYPES.find((t) => t.value === orderType);
    return (
      <Box sx={{ mt: 1 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            mb: 2,
            p: 1.2,
            bgcolor: currentType?.bg,
            borderRadius: 2,
            border: `1px solid ${currentType?.color}30`,
          }}
        >
          <Typography sx={{ fontSize: "1.3rem" }}>
            {currentType?.emoji}
          </Typography>
          <Box>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 700,
                color: currentType?.color,
                fontSize: "0.82rem",
              }}
            >
              {currentType?.label} Order
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: "#666", fontSize: "0.7rem" }}
            >
              Please fill the details below
            </Typography>
          </Box>
        </Box>

        <TextField
          fullWidth
          size="small"
          label="Your Name *"
          value={customerDetails.name}
          onChange={(e) =>
            setCustomerDetails({ ...customerDetails, name: e.target.value })
          }
          sx={{
            mb: 1.5,
            "& .MuiOutlinedInput-root": {
              borderRadius: 2,
              "&.Mui-focused fieldset": { borderColor: "#2e7d32" },
            },
          }}
        />
        <TextField
          fullWidth
          size="small"
          label="Phone Number *"
          type="tel"
          value={customerDetails.phone}
          onChange={(e) =>
            setCustomerDetails({ ...customerDetails, phone: e.target.value })
          }
          placeholder="10-digit mobile number"
          sx={{
            mb: 1.5,
            "& .MuiOutlinedInput-root": {
              borderRadius: 2,
              "&.Mui-focused fieldset": { borderColor: "#2e7d32" },
            },
          }}
        />

        {orderType === "DINE_IN" && (
          <TextField
            fullWidth
            size="small"
            label="Table Number *"
            value={customerDetails.tableNumber}
            onChange={(e) =>
              setCustomerDetails({
                ...customerDetails,
                tableNumber: e.target.value,
              })
            }
            placeholder="e.g., 5, 12, A1"
            sx={{
              mb: 1.5,
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                "&.Mui-focused fieldset": { borderColor: "#2e7d32" },
              },
            }}
          />
        )}

        {orderType === "DELIVERY" && (
          <TextField
            fullWidth
            size="small"
            label="Delivery Address *"
            value={customerDetails.address}
            onChange={(e) =>
              setCustomerDetails({
                ...customerDetails,
                address: e.target.value,
              })
            }
            multiline
            rows={3}
            placeholder="Full address with landmarks"
            sx={{
              mb: 1.5,
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                "&.Mui-focused fieldset": { borderColor: "#2e7d32" },
              },
            }}
          />
        )}

        {orderType === "TAKEAWAY" && (
          <Box
            sx={{
              p: 1.5,
              bgcolor: "#e3f2fd",
              borderRadius: 2,
              border: "1px solid #90caf9",
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <TakeoutDiningIcon sx={{ fontSize: 18, color: "#1565c0" }} />
            <Typography
              variant="caption"
              sx={{ color: "#1565c0", fontSize: "0.78rem" }}
            >
              🕐 Your order will be ready for pickup in {deliveryTime?.min}–
              {deliveryTime?.max} minutes
            </Typography>
          </Box>
        )}

        <Typography
          variant="caption"
          sx={{
            display: "block",
            textAlign: "right",
            color: "#888",
            mt: 1,
            fontSize: "0.65rem",
          }}
        >
          * Required fields
        </Typography>
      </Box>
    );
  };

  // ============================================
  // RENDER: PAYMENT
  // ============================================
  const renderPayment = () => {
    if (isUpdateMode && incrementalBalance === 0 && refundDueAmount > 0) {
      return (
        <Box
          sx={{
            mt: 2,
            p: 3,
            textAlign: "center",
            bgcolor: "#e8f5e9",
            borderRadius: 3,
            border: "1px solid #a5d6a7",
          }}
        >
          <MonetizationOnIcon sx={{ fontSize: 50, color: "#2e7d32", mb: 1 }} />
          <Typography variant="h6" fontWeight={800} color="#2e7d32">
            No Payment Required!
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mt: 1, maxWidth: 350, mx: "auto" }}
          >
            Your revised order amount is less than what you already paid. The
            restaurant will settle a refund of{" "}
            <b>₹{refundDueAmount.toFixed(2)}</b> to you.
          </Typography>
          <Chip
            label={`Refund Due: ₹${refundDueAmount.toFixed(2)}`}
            color="success"
            sx={{ mt: 2, fontWeight: 800, fontSize: "0.85rem", px: 1 }}
          />
        </Box>
      );
    }

    const availableMethods = PAYMENT_METHODS.filter((method) => {
      if (!paymentSettings) return true;
      if (method.value === "CASH") return paymentSettings.cashEnabled !== false;
      if (method.value === "UPI") return paymentSettings.upiEnabled !== false;
      if (method.value === "PHONEPE")
        return paymentSettings.phonePeEnabled !== false;
      return true;
    });

    return (
      <Box sx={{ mt: 1 }}>
        <Typography
          variant="body2"
          sx={{ fontWeight: 700, fontSize: "0.82rem", color: "#555", mb: 1.5 }}
        >
          Select Payment Method{" "}
          {isUpdateMode &&
            incrementalBalance > 0 &&
            `(For Extra ₹${incrementalBalance.toFixed(2)})`}
        </Typography>

        {loadingSettings ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={32} />
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {availableMethods.map((method) => {
              const isSelected = paymentMethod === method.value;
              let methodDetails = null;
              if (method.value === "UPI" && paymentSettings?.upiId) {
                methodDetails = {
                  id: paymentSettings.upiId,
                  qrCode: paymentSettings.upiQrCode,
                };
              }
              if (
                method.value === "PHONEPE" &&
                paymentSettings?.phonePeNumber
              ) {
                methodDetails = { number: paymentSettings.phonePeNumber };
              }

              return (
                <Paper
                  key={method.value}
                  elevation={isSelected ? 2 : 0}
                  onClick={() => setPaymentMethod(method.value)}
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    border: isSelected
                      ? `2px solid ${method.color}`
                      : "1px solid #e0e0e0",
                    bgcolor: isSelected ? method.bg : "white",
                    cursor: "pointer",
                    transition: "all 0.2s",
                    "&:hover": {
                      borderColor: method.color,
                      bgcolor: isSelected ? method.bg : "#f9f9f9",
                    },
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: "50%",
                        bgcolor: isSelected ? method.color : "#f5f5f5",
                        color: isSelected ? "white" : "#666",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {method.icon}
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 700,
                          color: isSelected ? method.color : "#333",
                        }}
                      >
                        {method.label}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: "#666", display: "block" }}
                      >
                        {method.description}
                      </Typography>
                      {/* {isSelected && methodDetails && (
                        <Box
                          sx={{
                            mt: 1,
                            p: 1,
                            bgcolor: "white",
                            borderRadius: 1,
                          }}
                        >
                          {method.value === "UPI" && (
                            <>
                              <Typography
                                variant="caption"
                                sx={{ fontWeight: 600, display: "block" }}
                              >
                                UPI ID: {methodDetails.id}
                              </Typography>
                              {methodDetails.qrCode && (
                                <Box sx={{ mt: 1, textAlign: "center" }}>
                                  <img
                                    src={methodDetails.qrCode}
                                    alt="UPI QR Code"
                                    style={{ maxWidth: 120, maxHeight: 120 }}
                                  />
                                </Box>
                              )}
                            </>
                          )}
                          {method.value === "PHONEPE" && (
                            <Typography
                              variant="caption"
                              sx={{ fontWeight: 600 }}
                            >
                              PhonePe Number: {methodDetails.number}
                            </Typography>
                          )}
                        </Box>
                      )} */}
                                            {isSelected && methodDetails && (
                        <Box
                          sx={{
                            mt: 1,
                            p: 1,
                            bgcolor: "white",
                            borderRadius: 1,
                            border: "1px solid #f0f0f0"
                          }}
                        >
                          {method.value === "UPI" && (
                            <>
                              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                <Typography
                                  variant="caption"
                                  sx={{ fontWeight: 600, display: "block", wordBreak: "break-all" }}
                                >
                                  UPI ID: {methodDetails.id}
                                </Typography>
                                <IconButton 
                                  size="small" 
                                  onClick={(e) => { e.stopPropagation(); handleCopy(methodDetails.id); }}
                                  sx={{ p: 0.5, ml: 1 }}
                                >
                                  {copiedText === methodDetails.id ? (
                                    <CheckCircleIcon sx={{ fontSize: 16, color: "#2e7d32" }} />
                                  ) : (
                                    <ContentCopyIcon sx={{ fontSize: 16, color: "#666" }} />
                                  )}
                                </IconButton>
                              </Box>
                              {methodDetails.qrCode && (
                                <Box sx={{ mt: 1, textAlign: "center" }}>
                                  <img
                                    src={methodDetails.qrCode}
                                    alt="UPI QR Code"
                                    style={{ maxWidth: 120, maxHeight: 120 }}
                                  />
                                </Box>
                              )}
                            </>
                          )}
                          {method.value === "PHONEPE" && (
                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                              <Typography
                                variant="caption"
                                sx={{ fontWeight: 600 }}
                              >
                                PhonePe: {methodDetails.number}
                              </Typography>
                              <IconButton 
                                size="small" 
                                onClick={(e) => { e.stopPropagation(); handleCopy(methodDetails.number); }}
                                sx={{ p: 0.5, ml: 1 }}
                              >
                                {copiedText === methodDetails.number ? (
                                  <CheckCircleIcon sx={{ fontSize: 16, color: "#2e7d32" }} />
                                ) : (
                                  <ContentCopyIcon sx={{ fontSize: 16, color: "#666" }} />
                                )}
                              </IconButton>
                            </Box>
                          )}
                        </Box>
                      )}
                    </Box>
                    <Radio checked={isSelected} sx={{ color: method.color }} />
                  </Box>
                </Paper>
              );
            })}
          </Box>
        )}
      </Box>
    );
  };

  // ============================================
  // RENDER: REVIEW
  // ============================================
  const renderReview = () => {
    const currentType = ORDER_TYPES.find((t) => t.value === orderType);
    const selectedPayment = PAYMENT_METHODS.find(
      (m) => m.value === paymentMethod,
    );
    const regularItems = cart.filter((item) => !isFreeItem(item));

    return (
      <Box sx={{ mt: 1 }}>
        {isUpdateMode && (
          <Box
            sx={{
              bgcolor: "#e3f2fd",
              border: "1px solid #64b5f6",
              borderRadius: 2,
              p: 1.5,
              mb: 2,
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <EditIcon sx={{ fontSize: 20, color: "#1565c0" }} />
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="body2"
                sx={{ fontWeight: 700, color: "#1565c0", fontSize: "0.82rem" }}
              >
                Update Mode
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: "#1565c0", fontSize: "0.7rem" }}
              >
                Make changes to order #{updateOrderId?.slice(-8).toUpperCase()}{" "}
                and click "Update Order"
              </Typography>
            </Box>
          </Box>
        )}

        <Box
          sx={{
            bgcolor: "#f9f9f9",
            borderRadius: 2,
            p: 1.5,
            border: "1px solid #f0f0f0",
            mb: 1.5,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              mb: 1,
              flexWrap: "wrap",
            }}
          >
            <Typography sx={{ fontSize: "1.2rem" }}>
              {currentType?.emoji}
            </Typography>
            <Chip
              label={currentType?.label}
              size="small"
              sx={{
                height: 20,
                fontSize: "0.68rem",
                fontWeight: 700,
                bgcolor: currentType?.bg,
                color: currentType?.color,
              }}
            />
            <Chip
              label={
                isUpdateMode && refundDueAmount > 0
                  ? "Refund Settlement"
                  : selectedPayment?.label || paymentMethod || "N/A"
              }
              size="small"
              sx={{
                height: 20,
                fontSize: "0.68rem",
                fontWeight: 700,
                bgcolor:
                  isUpdateMode && refundDueAmount > 0
                    ? "#e8f5e9"
                    : selectedPayment?.bg || "#f5f5f5",
                color:
                  isUpdateMode && refundDueAmount > 0
                    ? "#2e7d32"
                    : selectedPayment?.color || "#666",
              }}
            />
          </Box>

          <Box sx={{ display: "flex", gap: 0.5 }}>
            <Typography
              variant="caption"
              sx={{ color: "#888", fontSize: "0.75rem" }}
            >
              Name:
            </Typography>
            <Typography
              variant="caption"
              sx={{ fontWeight: 600, fontSize: "0.75rem" }}
            >
              {customerDetails.name}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 0.5 }}>
            <Typography
              variant="caption"
              sx={{ color: "#888", fontSize: "0.75rem" }}
            >
              Phone:
            </Typography>
            <Typography
              variant="caption"
              sx={{ fontWeight: 600, fontSize: "0.75rem" }}
            >
              {customerDetails.phone}
            </Typography>
          </Box>
          {orderType === "DINE_IN" && customerDetails.tableNumber && (
            <Box sx={{ display: "flex", gap: 0.5 }}>
              <Typography
                variant="caption"
                sx={{ color: "#888", fontSize: "0.75rem" }}
              >
                Table:
              </Typography>
              <Typography
                variant="caption"
                sx={{ fontWeight: 600, fontSize: "0.75rem" }}
              >
                {customerDetails.tableNumber}
              </Typography>
            </Box>
          )}
          {orderType === "DELIVERY" && customerDetails.address && (
            <Box sx={{ display: "flex", gap: 0.5 }}>
              <Typography
                variant="caption"
                sx={{ color: "#888", fontSize: "0.75rem" }}
              >
                Address:
              </Typography>
              <Typography
                variant="caption"
                sx={{ fontWeight: 600, fontSize: "0.75rem" }}
              >
                {customerDetails.address}
              </Typography>
            </Box>
          )}
        </Box>

        <Box
          sx={{
            bgcolor: "#f9f9f9",
            borderRadius: 2,
            p: 1.5,
            border: "1px solid #f0f0f0",
            mb: 1.5,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              fontWeight: 700,
              color: "#333",
              mb: 1,
              display: "block",
              fontSize: "0.75rem",
            }}
          >
            Order Items (
            {regularItems.reduce((sum, item) => sum + getQuantity(item), 0)})
          </Typography>

          {regularItems.map((item) => {
            const originalPrice = getOriginalPrice(item);
            const finalPrice = getFinalPrice(item);
            const quantity = getQuantity(item);
            const itemSubtotal = originalPrice * quantity;
            const itemTotal = finalPrice * quantity;
            const discountPerItem = originalPrice - finalPrice;

            return (
              <Box
                key={item._id}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  mb: 1,
                }}
              >
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.8rem",
                      display: "block",
                    }}
                  >
                    {item.itemName} × {quantity}
                  </Typography>
                  {discountPerItem > 0 && (
                    <Typography
                      variant="caption"
                      sx={{
                        color: "#2e7d32",
                        fontSize: "0.65rem",
                        fontWeight: 600,
                      }}
                    >
                      Save ₹{(discountPerItem * quantity).toFixed(2)}
                    </Typography>
                  )}
                </Box>
                <Box sx={{ textAlign: "right" }}>
                  {discountPerItem > 0 && (
                    <Typography
                      variant="caption"
                      sx={{
                        color: "#999",
                        fontSize: "0.7rem",
                        textDecoration: "line-through",
                        display: "block",
                      }}
                    >
                      ₹{itemSubtotal.toFixed(2)}
                    </Typography>
                  )}
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 700,
                      fontSize: "0.8rem",
                      color: discountPerItem > 0 ? "#2e7d32" : "inherit",
                    }}
                  >
                    ₹{itemTotal.toFixed(2)}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Box>

        {freeItemsFromOffers.length > 0 && (
          <Box
            sx={{
              bgcolor: "#e8f5e9",
              borderRadius: 2,
              p: 1.5,
              border: "1px solid #c8e6c9",
              mb: 1.5,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                fontWeight: 700,
                color: "#2e7d32",
                mb: 1,
                display: "block",
                fontSize: "0.75rem",
              }}
            >
              🎁 Free Items
            </Typography>
            {freeItemsFromOffers.map((item, index) => (
              <Box
                key={index}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 0.5,
                }}
              >
                <Typography variant="caption" sx={{ fontSize: "0.75rem" }}>
                  {item.itemName} × {item.quantity}
                </Typography>
                <Chip
                  label={`FREE (₹${((item.price || item.originalPrice || 0) * item.quantity).toFixed(2)} saved)`}
                  size="small"
                  sx={{
                    height: 18,
                    fontSize: "0.6rem",
                    bgcolor: "#2e7d32",
                    color: "white",
                  }}
                />
              </Box>
            ))}
          </Box>
        )}

        <Box
          sx={{
            bgcolor: "#f9f9f9",
            borderRadius: 2,
            p: 1.5,
            border: "1px solid #f0f0f0",
            mb: 1.5,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              fontWeight: 700,
              color: "#333",
              mb: 1,
              display: "block",
              fontSize: "0.75rem",
            }}
          >
            Payment Summary
          </Typography>

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              mb: 0.5,
            }}
          >
            <Typography
              variant="caption"
              sx={{ color: "#666", fontSize: "0.78rem" }}
            >
              Subtotal
            </Typography>
            <Typography
              variant="caption"
              sx={{ fontWeight: 600, fontSize: "0.78rem" }}
            >
              ₹{subtotal.toFixed(2)}
            </Typography>
          </Box>

          {itemDiscountsAmount > 0 && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                mb: 0.5,
              }}
            >
              <Typography
                variant="caption"
                sx={{ color: "#2e7d32", fontSize: "0.78rem" }}
              >
                Item discounts
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: "#2e7d32",
                  fontWeight: 700,
                  fontSize: "0.78rem",
                }}
              >
                -₹{itemDiscountsAmount.toFixed(2)}
              </Typography>
            </Box>
          )}

          {bogoDiscountAmount > 0 && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                mb: 0.5,
              }}
            >
              <Typography
                variant="caption"
                sx={{ color: "#2e7d32", fontSize: "0.78rem" }}
              >
                BOGO offer
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: "#2e7d32",
                  fontWeight: 700,
                  fontSize: "0.78rem",
                }}
              >
                {formattedBogoDiscount}
              </Typography>
            </Box>
          )}

          {promoDiscountAmount > 0 && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                mb: 0.5,
              }}
            >
              <Typography
                variant="caption"
                sx={{ color: "#2e7d32", fontSize: "0.78rem" }}
              >
                Promo discount
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: "#2e7d32",
                  fontWeight: 700,
                  fontSize: "0.78rem",
                }}
              >
                {formattedPromoDiscount}
              </Typography>
            </Box>
          )}

          {orderType === "DELIVERY" && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                mb: 0.6,
              }}
            >
              <Typography
                variant="caption"
                sx={{ color: "#666", fontSize: "0.8rem" }}
              >
                Delivery Fee
              </Typography>
              <Typography
                variant="caption"
                sx={{ fontWeight: 700, fontSize: "0.8rem" }}
              >
                {deliveryFee === 0 ? (
                  <span style={{ color: "#2e7d32" }}>FREE</span>
                ) : (
                  `₹${deliveryFee.toFixed(2)}`
                )}
              </Typography>
            </Box>
          )}
          <Divider sx={{ my: 0.8 }} />

          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Typography
              variant="body2"
              sx={{ fontWeight: 700, fontSize: "0.85rem" }}
            >
              Total to Pay
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 800,
                color: "#1a1a1a",
                fontSize: "0.9rem",
              }}
            >
              {formattedTotal}
            </Typography>
          </Box>

          {isUpdateMode && previouslyPaidAmount > 0 && (
            <Box
              sx={{
                mt: 1.5,
                p: 1.2,
                bgcolor: "#fff8f0",
                borderRadius: 2,
                border: "1px dashed #FF6B00",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  mb: 0.5,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{ color: "#666", fontSize: "0.78rem" }}
                >
                  Already Paid Earlier:
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 700,
                    color: "#2e7d32",
                    fontSize: "0.78rem",
                  }}
                >
                  -₹{previouslyPaidAmount.toFixed(2)}
                </Typography>
              </Box>

              <Divider sx={{ my: 0.5, borderColor: "#FF6B0030" }} />

              {incrementalBalance > 0 ? (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 800,
                      color: "#e65100",
                      fontSize: "0.85rem",
                    }}
                  >
                    Remaining to Pay:
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 800,
                      color: "#e65100",
                      fontSize: "0.95rem",
                    }}
                  >
                    ₹{incrementalBalance.toFixed(2)}
                  </Typography>
                </Box>
              ) : refundDueAmount > 0 ? (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 800,
                      color: "#2e7d32",
                      fontSize: "0.85rem",
                    }}
                  >
                    Refund Due from Restaurant:
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 800,
                      color: "#2e7d32",
                      fontSize: "0.95rem",
                    }}
                  >
                    ₹{refundDueAmount.toFixed(2)}
                  </Typography>
                </Box>
              ) : (
                <Typography
                  variant="caption"
                  sx={{ color: "#2e7d32", fontWeight: 700, display: "block" }}
                >
                  ✓ Full bill adjusted. No payment needed!
                </Typography>
              )}
            </Box>
          )}
        </Box>

        {restaurantInfo?.isWhatsAppNotifications !== false ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              p: 1.2,
              bgcolor: "#e8f5e9",
              borderRadius: 2,
              border: "1px solid #c8e6c9",
            }}
          >
            <WhatsAppIcon sx={{ fontSize: 18, color: "#25D366" }} />
            <Typography
              variant="caption"
              sx={{ color: "#2e7d32", fontWeight: 600, fontSize: "0.75rem" }}
            >
              Order will be sent to restaurant via WhatsApp for confirmation
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              p: 1.2,
              bgcolor: "#e3f2fd",
              borderRadius: 2,
              border: "1px solid #90caf9",
            }}
          >
            <CheckCircleIcon sx={{ fontSize: 18, color: "#1565c0" }} />
            <Typography
              variant="caption"
              sx={{ color: "#1565c0", fontWeight: 600, fontSize: "0.75rem" }}
            >
              Order will be placed directly in the system
            </Typography>
          </Box>
        )}
      </Box>
    );
  };

  // ============================================
  // MAIN RENDER
  // ============================================
  return (
    <>
      <Drawer
        anchor="bottom"
        open={open}
        onClose={() => {
          handleReset();
          onClose();
        }}
        PaperProps={{
          sx: {
            borderTopLeftRadius: 20,
            borderTopRightRadius: 20,
            maxHeight: "92vh",
            overflow: "hidden",
          },
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "center", pt: 1, pb: 0.5 }}>
          <Box
            sx={{ width: 36, height: 4, borderRadius: 2, bgcolor: "#e0e0e0" }}
          />
        </Box>
        <Box sx={{ px: 2, pb: 2, overflow: "auto", maxHeight: "88vh" }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 1,
            }}
          >
            <Box>
              <Typography
                variant="h6"
                sx={{ fontWeight: 800, fontSize: "1rem", color: "#1a1a1a" }}
              >
                {activeStep === 0 && "Your Cart"}
                {activeStep === 1 && "Order Type"}
                {activeStep === 2 && "Your Details"}
                {activeStep === 3 && "Payment Method"}
                {activeStep === 4 &&
                  (isUpdateMode ? "Update Order" : "Review Order")}
              </Typography>
              {restaurantInfo && activeStep === 0 && (
                <Typography
                  variant="caption"
                  sx={{ color: "#888", fontSize: "0.72rem" }}
                >
                  {restaurantInfo.restaurantName}
                </Typography>
              )}
            </Box>
            <IconButton
              size="small"
              onClick={() => {
                handleReset();
                onClose();
              }}
              disabled={isPlacingOrder}
              sx={{ bgcolor: "#f5f5f5", "&:hover": { bgcolor: "#ebebeb" } }}
            >
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>

          {activeStep > 0 && (
            <Box sx={{ mb: 2 }}>
              <Stepper
                activeStep={activeStep - 1}
                alternativeLabel
                sx={{
                  "& .MuiStepLabel-label": { fontSize: "0.65rem", mt: 0.3 },
                  "& .MuiStepIcon-root.Mui-active": { color: "#2e7d32" },
                  "& .MuiStepIcon-root.Mui-completed": { color: "#2e7d32" },
                }}
              >
                <Step key="order-type">
                  <StepLabel>Order Type</StepLabel>
                </Step>
                <Step key="details">
                  <StepLabel>Details</StepLabel>
                </Step>
                <Step
                  key="payment"
                  completed={isUpdateMode && refundDueAmount > 0}
                >
                  <StepLabel>
                    {isUpdateMode && refundDueAmount > 0 ? "Refund" : "Payment"}
                  </StepLabel>
                </Step>
                <Step key="review">
                  <StepLabel>Review</StepLabel>
                </Step>
              </Stepper>
            </Box>
          )}

          {activeStep === 0 && renderCart()}
          {activeStep === 1 && renderOrderType()}
          {activeStep === 2 && renderDetails()}
          {activeStep === 3 && renderPayment()}
          {activeStep === 4 && renderReview()}

          <Box sx={{ display: "flex", gap: 1.2, mt: 2.5 }}>
            {activeStep > 0 && activeStep < 5 && (
              <Button
                variant="outlined"
                onClick={handleBack}
                disabled={isPlacingOrder}
                sx={{
                  flex: 1,
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 700,
                  borderColor: "#e0e0e0",
                  color: "#555",
                  fontSize: "0.85rem",
                }}
              >
                Back
              </Button>
            )}
            {activeStep === 0 && cart.length > 0 && (
              <Button
                variant="outlined"
                onClick={clearCompleteCart}
                sx={{
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 700,
                  borderColor: "#f44336",
                  color: "#f44336",
                  fontSize: "0.85rem",
                  "&:hover": { bgcolor: "#ffebee" },
                  px: 1.5,
                }}
              >
                Clear
              </Button>
            )}

            {activeStep === 0 && (
              <Button
                fullWidth
                variant="contained"
                disabled={cart.length === 0}
                onClick={() => {
                  if (isUpdateMode && refundDueAmount > 0) {
                    setActiveStep(4);
                  } else {
                    setActiveStep(1);
                  }
                }}
                sx={{
                  flex: 2,
                  borderRadius: 2,
                  bgcolor: isUpdateMode ? "#1976d2" : "#2e7d32",
                  "&:hover": { bgcolor: isUpdateMode ? "#1565c0" : "#1b5e20" },
                  textTransform: "none",
                  fontWeight: 800,
                  fontSize: "0.92rem",
                  py: 1.3,
                  boxShadow: isUpdateMode
                    ? "0 3px 12px rgba(25,118,210,0.35)"
                    : "0 3px 12px rgba(46,125,50,0.35)",
                }}
              >
                {cart.length === 0
                  ? "Cart is Empty"
                  : isUpdateMode
                    ? previouslyPaidAmount > 0 && incrementalBalance > 0
                      ? `Proceed ₹${incrementalBalance.toFixed(2)} to Pay`
                      : refundDueAmount > 0
                        ? `Review Update (Refund ₹${refundDueAmount.toFixed(2)})`
                        : "Proceed to Update"
                    : `Proceed ${formattedTotal}`}
              </Button>
            )}

            {activeStep === 2 && (
              <Button
                variant="contained"
                onClick={handleNext}
                sx={{
                  flex: 2,
                  borderRadius: 2,
                  bgcolor: "#2e7d32",
                  "&:hover": { bgcolor: "#1b5e20" },
                  textTransform: "none",
                  fontWeight: 800,
                  fontSize: "0.88rem",
                  py: 1.3,
                }}
              >
                {isUpdateMode && incrementalBalance === 0
                  ? "Review Order"
                  : "Continue to Payment"}
              </Button>
            )}

            {activeStep === 3 && (
              <Button
                variant="contained"
                onClick={handleNext}
                disabled={
                  !isUpdateMode
                    ? !paymentMethod
                    : incrementalBalance > 0 && !paymentMethod
                }
                sx={{
                  flex: 2,
                  borderRadius: 2,
                  bgcolor: "#2e7d32",
                  "&:hover": { bgcolor: "#1b5e20" },
                  textTransform: "none",
                  fontWeight: 800,
                  fontSize: "0.88rem",
                  py: 1.3,
                }}
              >
                Review Order
              </Button>
            )}

            {activeStep === 4 && (
              <Button
                variant="contained"
                onClick={placeOrder}
                disabled={isPlacingOrder}
                startIcon={
                  isPlacingOrder ? (
                    <CircularProgress size={16} sx={{ color: "white" }} />
                  ) : isUpdateMode ? (
                    <EditIcon />
                  ) : restaurantInfo?.isWhatsAppNotifications !== false ? (
                    <WhatsAppIcon />
                  ) : (
                    <CheckCircleIcon />
                  )
                }
                sx={{
                  flex: 2,
                  borderRadius: 2,
                  bgcolor: isUpdateMode
                    ? refundDueAmount > 0
                      ? "#2e7d32"
                      : "#1976d2"
                    : restaurantInfo?.isWhatsAppNotifications !== false
                      ? "#25D366"
                      : "#2e7d32",
                  "&:hover": {
                    bgcolor: isUpdateMode
                      ? refundDueAmount > 0
                        ? "#1b5e20"
                        : "#1565c0"
                      : restaurantInfo?.isWhatsAppNotifications !== false
                        ? "#1ebe5a"
                        : "#1b5e20",
                  },
                  textTransform: "none",
                  fontWeight: 800,
                  fontSize: "0.88rem",
                  py: 1.3,
                  boxShadow: isUpdateMode
                    ? "0 3px 12px rgba(25,118,210,0.35)"
                    : restaurantInfo?.isWhatsAppNotifications !== false
                      ? "0 3px 12px rgba(37,211,102,0.35)"
                      : "0 3px 12px rgba(46,125,50,0.35)",
                }}
              >
                {isPlacingOrder
                  ? "Processing..."
                  : isUpdateMode
                    ? previouslyPaidAmount > 0 && incrementalBalance > 0
                      ? `Pay Extra ₹${incrementalBalance.toFixed(2)} & Update`
                      : refundDueAmount > 0
                        ? `Update Order (Refund Due ₹${refundDueAmount.toFixed(2)})`
                        : "Update Order"
                    : restaurantInfo?.isWhatsAppNotifications !== false
                      ? `Place Order ${formattedTotal}`
                      : `Confirm Order ${formattedTotal}`}
              </Button>
            )}
          </Box>
        </Box>
      </Drawer>

      <Snackbar
        open={toast.open}
        autoHideDuration={2500}
        onClose={() => setToast({ ...toast, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        sx={{ bottom: 70 }}
      >
        <Alert
          severity={toast.severity}
          variant="filled"
          sx={{ borderRadius: 2, fontSize: "0.8rem" }}
        >
          {toast.msg}
        </Alert>
      </Snackbar>
    </>
  );
};

export default CartDrawer;
